import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { injectIntl } from 'react-intl';
import Joyride, { STATUS, ACTIONS, EVENTS } from 'react-joyride';
import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import {
    endTour,
    setTourStepIndex,
    markStepCompleteRemote,
    openChecklist,
} from '../../../redux/actions/onboarding';
import {
    STEP_IDS,
    TOUR_IDS,
    resolveTourById,
    waitForTarget,
} from '../configs/onboarding.config';
import TourMenuPath from '../tour-menu-path';
import '../onboarding-overlays.css';

// Steps cuja conclusao NAO e detectada via outbox (precisam endpoint manual).
// Mantido em sync com OnboardingStepCodes.ManuallyMarkedSteps no backend.
const MANUALLY_MARKED_STEP_IDS = new Set([
    STEP_IDS.CONFIGURE_ORGANIZATION,
    STEP_IDS.LEARN_AUTOMATION_TEMPLATES,
    STEP_IDS.CLASSIFY_FINISHED_TICKET,
]);

// Resolve tour ativo a partir do tourId no redux state + demoSeedRefs.
// resolveTourById ja sabe mapear tourId -> tour (builders dinamicos
// via switch, ou lookup direto em TOURS_BY_ID no default). O tourId ja
// chega aqui resolvido pra variante certa (base ou *-requester) — quem
// decide isso e o ChecklistFab, no momento de iniciar o tour.
function getActiveTour(activeTourId, demoSeedRefs) {
    if (!activeTourId) return null;
    return resolveTourById(activeTourId, demoSeedRefs);
}

// Aplica intl.formatMessage em titleKey/contentKey, mantendo os campos
// `title` e `content` que o Joyride consome. Steps com title/content
// literais (sem *Key) passam direto.
function resolveStepTexts(steps, intl) {
    if (!Array.isArray(steps)) return [];
    return steps.map((s) => {
        const out = { ...s };
        if (s.titleKey) out.title = intl.formatMessage({ id: s.titleKey });
        if (s.contentKey) out.content = intl.formatMessage({ id: s.contentKey });
        return out;
    });
}

// =====================================================================
// TourTooltip — custom tooltip do Joyride.
// Diferencial vs. tooltip default: renderiza <TourMenuPath> acima do
// titulo quando step.menuPath estiver presente. Mantem a aparencia
// alinhada ao restante do onboarding (mesma paleta, MUI components).
// =====================================================================
const TourTooltip = ({
    continuous, index, size, step,
    backProps, closeProps, primaryProps, skipProps, tooltipProps,
    isLastStep, intl,
}) => {
    const showSkip = !isLastStep;
    const nextLabel = isLastStep
        ? intl.formatMessage({ id: 'onboarding.tour.last' })
        : (continuous && size > 1
            ? intl.formatMessage(
                { id: 'onboarding.tour.nextWithProgress' },
                { step: index + 1, steps: size }
            )
            : intl.formatMessage({ id: 'onboarding.tour.next' }));

    return (
        <Box
            {...tooltipProps}
            sx={{
                width: 380,
                maxWidth: '92vw',
                backgroundColor: '#ffffff',
                borderRadius: '14px',
                boxShadow: '0 18px 40px rgba(15, 23, 42, 0.18), 0 4px 12px rgba(15, 23, 42, 0.06)',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <IconButton
                {...closeProps}
                size="small"
                aria-label={intl.formatMessage({ id: 'onboarding.tour.close' })}
                sx={{
                    position: 'absolute', top: 8, right: 8,
                    color: '#9aa0a6', zIndex: 1,
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                }}
            >
                <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>

            <Box sx={{ padding: '24px 24px 0 24px' }}>
                {Array.isArray(step?.menuPath) && step.menuPath.length > 0 && (
                    <TourMenuPath path={step.menuPath} intl={intl} />
                )}

                {step?.title && (
                    <Typography
                        component="h2"
                        sx={{
                            fontSize: 16, fontWeight: 700, color: '#1f2937',
                            margin: 0, mb: 1, lineHeight: 1.35, pr: 3,
                        }}
                    >
                        {step.title}
                    </Typography>
                )}

                {step?.content && (
                    <Typography
                        component="div"
                        sx={{
                            fontSize: 14, color: '#4b5563',
                            lineHeight: 1.55, mt: 0,
                        }}
                    >
                        {step.content}
                    </Typography>
                )}
            </Box>

            <Box
                sx={{
                    mt: 3, padding: '14px 24px',
                    borderTop: '1px solid #eef0f2',
                    backgroundColor: '#fafbfc',
                    display: 'flex', alignItems: 'center', gap: 1,
                }}
            >
                {showSkip && (
                    <Button
                        {...skipProps}
                        size="small"
                        sx={{
                            color: '#6b7280', fontSize: 13, fontWeight: 500,
                            textTransform: 'none', minWidth: 'auto',
                        }}
                    >
                        {intl.formatMessage({ id: 'onboarding.tour.skip' })}
                    </Button>
                )}
                <Box sx={{ flex: 1 }} />
                {index > 0 && (
                    <Button
                        {...backProps}
                        size="small"
                        sx={{
                            color: '#4b5563', fontSize: 13, fontWeight: 500,
                            textTransform: 'none', minWidth: 'auto', mr: 1,
                        }}
                    >
                        {intl.formatMessage({ id: 'onboarding.tour.back' })}
                    </Button>
                )}
                <Button
                    {...primaryProps}
                    variant="contained"
                    size="small"
                    sx={{
                        backgroundColor: '#8BC34A', color: '#ffffff',
                        borderRadius: '8px', padding: '6px 16px',
                        fontSize: 13, fontWeight: 600, textTransform: 'none',
                        boxShadow: '0 2px 6px rgba(139, 195, 74, 0.35)',
                        '&:hover': { backgroundColor: '#7cb342' },
                    }}
                >
                    {nextLabel}
                </Button>
            </Box>
        </Box>
    );
};

// Paleta — alinhada com o checklist drawer
const TOUR_COLORS = {
    primary: '#8BC34A',
    primaryDark: '#7cb342',
    text: '#1f2937',
    textSecondary: '#4b5563',
    textMuted: '#6b7280',
    overlay: 'rgba(15, 23, 42, 0.55)',
    arrow: '#ffffff',
    spotlightShadow: '0 0 0 4px rgba(139, 195, 74, 0.25), 0 0 18px rgba(0, 0, 0, 0.45)',
};

// Tempo MAXIMO que o tour aguarda um anchor aparecer em TARGET_NOT_FOUND.
// Apos esse tempo, PULA pro proximo step em vez de travar.
//
// 1500ms cobre:
//   - Transicao CSS do MUI Drawer (~300ms)
//   - React re-render apos state change (~16-50ms por frame)
//   - Latencia de prod / DataGrid hidratacao (~500-1000ms)
//
// Sem overlay visual — pula silenciosamente. Steps que sao informativos
// (System Overview etc.) toleram pular sem prejuizo do tour. Steps com
// dependencia real de elemento (force-click) usam `runOnAdvance.waitForSelector`
// que tem seu proprio caminho (nao passa por TNF).
const WAIT_BEFORE_OVERLAY_MS = 1500;

class ProductTour extends Component {
    state = { waitingForTarget: null };

    // Force click "React-style" — dispara dispatchEvent COM bubbles + click()
    // nativo. Combinacao garante que tanto handlers React (synthetic events)
    // quanto handlers DOM nativos sejam acionados, em qualquer combinacao
    // de componente (MUI, custom, lazy components).
    static forceClickReactStyle = (el) => {
        if (!el) return false;
        let ok = false;
        try {
            const evt = new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window,
            });
            el.dispatchEvent(evt);
            ok = true;
        } catch (_) { /* swallow */ }
        try {
            el.click();
            ok = true;
        } catch (_) { /* swallow */ }
        return ok;
    };

    // Scroll suave pro elemento ficar centralizado antes do balao do Joyride
    // calcular posicionamento. Containers com scroll proprio sao respeitados.
    static scrollIntoCenter = (el) => {
        if (!el || typeof el.scrollIntoView !== 'function') return;
        try {
            el.scrollIntoView({ block: 'center', inline: 'center', behavior: 'auto' });
        } catch (_) { /* swallow */ }
    };

    handleCallback = async (data) => {
        const { status, action, type, step, index } = data;
        const { activeTourId, demoSeedRefs } = this.props.onboarding || {};
        const tour = getActiveTour(activeTourId, demoSeedRefs);

        // TARGET_NOT_FOUND: aguarda elemento aparecer.
        //
        // Estrategia (revista):
        //  1) Tentativa silenciosa por WAIT_BEFORE_OVERLAY_MS (1500ms) —
        //     cobre transicao CSS do Drawer + re-render + latencia prod.
        //  2) Se nao apareceu, PULA SILENCIOSAMENTE pro proximo step
        //     em vez de mostrar WaitingOverlay e travar.
        //
        // Por que pular em vez de aguardar+overlay?
        //  - Para tours informativos (System Overview, etc.) o step e anchor de
        //    layout (topbar/sidebar). Se nao apareceu em 400ms, alguma coisa nao
        //    casou e nao vai casar — melhor seguir o tour que travar 60s.
        //  - Para tours que dependem de force-click + navegacao (Assumir/Encerrar/
        //    Configurar Org/etc.), o `runOnAdvance.waitForSelector` ja tem seu
        //    proprio waitForTarget com timeout — esse caminho nao passa por TNF.
        //  - WaitingOverlay original tinha bug de travar em telas com SignalR +
        //    timers + polling (DOM constantemente mutando, MUI Drawer persistent
        //    com isVisible sempre true mas sem casamento real do Joyride).
        if (type === EVENTS.TARGET_NOT_FOUND) {
            const targetSel = step?.target;
            if (typeof targetSel !== 'string' || targetSel === 'body') {
                this.finishTour();
                return;
            }

            // Fase 1: tenta achar rapido, sem overlay
            const quickFound = await waitForTarget(targetSel, { timeout: WAIT_BEFORE_OVERLAY_MS });
            if (!this.props.onboarding?.activeTourId) return;

            if (quickFound) {
                const el = document.querySelector(targetSel);
                ProductTour.scrollIntoCenter(el);
                this.props.setTourStepIndex(index);
                return;
            }

            // Fase 2: nao achou — pula step silenciosamente.
            // eslint-disable-next-line no-console
            console.warn(
                '[ProductTour] step=%s sem target em %dms, pulando: %s',
                index, WAIT_BEFORE_OVERLAY_MS, targetSel,
            );

            const totalSteps = tour?.steps?.length ?? 0;
            const nextIndex = index + 1;
            if (nextIndex < totalSteps) {
                this.props.setTourStepIndex(nextIndex);
            } else {
                this.finishTour();
            }
            return;
        }

        // Step renderizou OK — garante target no viewport
        if (type === EVENTS.STEP_BEFORE) {
            if (typeof step?.target === 'string' && step.target !== 'body') {
                const el = document.querySelector(step.target);
                ProductTour.scrollIntoCenter(el);
            }
        }

        // STEP_AFTER + ACTION.NEXT/PREV: controlamos manualmente a transicao.
        // Aqui executa onAdvance (clickSelector + waitForSelector) se houver,
        // depois move stepIndex.
        if (type === EVENTS.STEP_AFTER) {
            if (action === ACTIONS.NEXT) {
                const currentStep = tour?.steps?.[index];
                const isLastStep = tour && index >= tour.steps.length - 1;

                if (isLastStep) {
                    // Concluiu o ultimo step
                    this.finishTour();
                    return;
                }

                // Executa onAdvance (forca click + aguarda target do proximo step)
                if (currentStep?.onAdvance) {
                    const ok = await this.runOnAdvance(currentStep.onAdvance);
                    if (!ok) {
                        // eslint-disable-next-line no-console
                        console.error(
                            '[ProductTour] onAdvance falhou no step %s — encerrando tour.',
                            index
                        );
                        this.finishTour();
                        return;
                    }
                }

                this.props.setTourStepIndex(index + 1);
                return;
            }

            if (action === ACTIONS.PREV) {
                this.props.setTourStepIndex(Math.max(0, index - 1));
                return;
            }

            if (action === ACTIONS.CLOSE) {
                // X ou Esc
                this.finishTour();
                return;
            }
        }

        // Skip (botao Pular)
        if (status === STATUS.SKIPPED) {
            this.finishTour();
            return;
        }

        // FINISHED via Joyride (geralmente ja tratado em STEP_AFTER+NEXT do ultimo step,
        // mas mantem como fallback)
        if (status === STATUS.FINISHED) {
            this.finishTour();
        }
    };

    runOnAdvance = async (onAdvance) => {
        const { clickSelector, waitForSelector, waitTimeoutMs = 5000, precondition, dispatchAction } = onAdvance || {};

        // 0) Precondition opcional — funcao que retorna true se o click DEVE rolar.
        // Uso classico: forcar abrir sidebar/drawer SE fechado, sem fechar se ja aberto.
        // Ex: precondition: () => !document.querySelector('[data-tour="sidebar-paper"]')?.offsetParent
        if (typeof precondition === 'function') {
            try {
                const shouldClick = precondition();
                if (!shouldClick) {
                    // Pula click — drawer ja aberto ou outra condicao satisfeita
                    if (waitForSelector) {
                        const found = await waitForTarget(waitForSelector, { timeout: waitTimeoutMs });
                        if (found) {
                            const next = document.querySelector(waitForSelector);
                            ProductTour.scrollIntoCenter(next);
                        }
                    }
                    return true;
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('[ProductTour] precondition lancou excecao:', err);
            }
        }

        // 0.5) dispatchAction opcional — chama uma Redux action pura em vez de
        // simular click. Mais robusto pra abrir drawers/sidebars que dependem
        // de Redux state (ex.: toggleActionMenu(true) abre drawer Acoes Rapidas
        // direto, sem depender de event propagation no IconButton MUI).
        // Quando presente, executa ANTES do clickSelector (que pode ser pulado).
        if (typeof dispatchAction === 'function') {
            try {
                const action = dispatchAction();
                if (action && this.props.dispatch) {
                    this.props.dispatch(action);
                }
                if (waitForSelector) {
                    const found = await waitForTarget(waitForSelector, { timeout: waitTimeoutMs });
                    if (found) {
                        const next = document.querySelector(waitForSelector);
                        ProductTour.scrollIntoCenter(next);
                    }
                }
                return true;
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('[ProductTour] dispatchAction lancou excecao:', err);
                return false;
            }
        }

        // 1) Forca click — usa dispatchEvent React-style pra MUI/Material-UI
        // capturar via synthetic events (el.click() nativo perde alguns handlers).
        if (clickSelector) {
            const el = document.querySelector(clickSelector);
            if (!el) {
                // eslint-disable-next-line no-console
                console.warn('[ProductTour] onAdvance.clickSelector nao encontrado:', clickSelector);
                return false;
            }
            ProductTour.scrollIntoCenter(el);
            const ok = ProductTour.forceClickReactStyle(el);
            if (!ok) {
                // eslint-disable-next-line no-console
                console.warn('[ProductTour] Falha no click programatico em:', clickSelector);
                return false;
            }
        }

        // 2) Aguarda alvo do proximo step aparecer + DOM estavel
        // (waitForTarget v2 com MutationObserver + 250ms de estabilidade).
        if (waitForSelector) {
            const found = await waitForTarget(waitForSelector, { timeout: waitTimeoutMs });
            if (!found) {
                // eslint-disable-next-line no-console
                console.warn(
                    '[ProductTour] onAdvance.waitForSelector nao apareceu em %dms: %s',
                    waitTimeoutMs, waitForSelector,
                );
                return false;
            }
            // Garante viewport antes do balao posicionar
            const next = document.querySelector(waitForSelector);
            ProductTour.scrollIntoCenter(next);
        }

        return true;
    };

    finishTour = () => {
        const { activeTourId, demoSeedRefs } = this.props.onboarding || {};
        const tour = getActiveTour(activeTourId, demoSeedRefs);

        // Cleanup opcional do tour (ex.: fechar drawer/modal aberto durante o tour)
        if (tour?.onCleanup) {
            try { tour.onCleanup(); } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('[ProductTour] onCleanup falhou:', err);
            }
        }

        // Steps manualmente marcados (sem evento de dominio em outbox)
        // disparam o endpoint /api/onboarding/me/step/{id}/complete.
        // tourId == stepId so vale hoje pros steps de MANUALLY_MARKED_STEP_IDS
        // (nenhum deles tem requesterTourId). Se um step manualmente marcado
        // ganhar variante de audience, esse .has() para de bater silenciosamente
        // pro requester.
        if (activeTourId && MANUALLY_MARKED_STEP_IDS.has(activeTourId)) {
            this.props.markStepCompleteRemote(activeTourId);
        }

        // Apos o System Overview, abre o checklist drawer pra mostrar
        // os passos do onboarding (fluxo natural: panorama -> roteiro).
        const wasSystemOverview = activeTourId === TOUR_IDS.SYSTEM_OVERVIEW;

        this.props.endTour();

        if (wasSystemOverview) {
            // Pequeno atraso pra Joyride desmontar antes do drawer abrir
            setTimeout(() => this.props.openChecklist(), 200);
        }
    };

    render() {
        const { activeTourId, currentTourStepIndex, demoSeedRefs } = this.props.onboarding || {};
        if (!activeTourId) return null;

        const tour = getActiveTour(activeTourId, demoSeedRefs);
        if (!tour) return null;

        const { intl } = this.props;
        const { waitingForTarget } = this.state;
        const resolvedSteps = resolveStepTexts(tour.steps, intl);

        // Wrapper pra injetar intl no TourTooltip custom (Joyride nao passa context).
        const TooltipWithIntl = (tooltipProps) => (
            <TourTooltip {...tooltipProps} intl={intl} />
        );

        return (
            <>
            {waitingForTarget && (
                <WaitingOverlay
                    intl={intl}
                    onCancel={() => {
                        this.setState({ waitingForTarget: null });
                        this.finishTour();
                    }}
                />
            )}
            <Joyride
                steps={resolvedSteps}
                run
                continuous
                stepIndex={currentTourStepIndex || 0}
                showProgress
                showSkipButton
                scrollToFirstStep
                scrollOffset={80}
                spotlightPadding={12}
                disableOverlayClose
                disableCloseOnEsc={false}
                callback={this.handleCallback}
                tooltipComponent={TooltipWithIntl}
                floaterProps={{
                    styles: {
                        floater: {
                            filter: 'drop-shadow(0 12px 32px rgba(15, 23, 42, 0.18))',
                        },
                    },
                }}
                styles={{
                    options: {
                        primaryColor: TOUR_COLORS.primary,
                        zIndex: 10000,
                        arrowColor: '#ffffff',
                        overlayColor: TOUR_COLORS.overlay,
                        spotlightShadow: TOUR_COLORS.spotlightShadow,
                    },
                    beacon: { outline: 'none' },
                    beaconInner: { backgroundColor: TOUR_COLORS.primary },
                    beaconOuter: {
                        backgroundColor: 'rgba(139, 195, 74, 0.4)',
                        borderColor: TOUR_COLORS.primary,
                    },
                }}
            />
            </>
        );
    }
}

// =====================================================================
// WaitingOverlay — overlay visual mostrado enquanto o tour aguarda um
// elemento aparecer na tela (DataGrid, Drawer, modal). Da feedback ao
// usuario em vez de ficar com tela parada e sem indicacao.
//
// Botao Cancelar permite o user encerrar o tour se nao quiser esperar.
// =====================================================================
const WaitingOverlay = ({ intl, onCancel }) => (
    <Box
        sx={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 10001,
            backgroundColor: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}
    >
        <Box
            sx={{
                backgroundColor: '#ffffff',
                borderRadius: '14px',
                padding: '28px 36px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                boxShadow: '0 18px 40px rgba(15, 23, 42, 0.25)',
                maxWidth: 360,
            }}
        >
            <CircularProgress size={36} sx={{ color: '#8BC34A' }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1f2937', textAlign: 'center' }}>
                {intl.formatMessage({ id: 'onboarding.tour.waitingTarget.title' })}
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#4b5563', textAlign: 'center', lineHeight: 1.5 }}>
                {intl.formatMessage({ id: 'onboarding.tour.waitingTarget.description' })}
            </Typography>
            <Button
                onClick={onCancel}
                size="small"
                sx={{
                    color: '#6b7280', fontSize: 13, fontWeight: 500,
                    textTransform: 'none', mt: 1,
                }}
            >
                {intl.formatMessage({ id: 'onboarding.tour.waitingTarget.cancel' })}
            </Button>
        </Box>
    </Box>
);

const mapStateToProps = (state) => ({
    onboarding: state.onboarding,
});

const mapDispatchToProps = (dispatch) => ({
    ...bindActionCreators(
        { endTour, setTourStepIndex, markStepCompleteRemote, openChecklist },
        dispatch,
    ),
    // Expoe dispatch puro pra onAdvance.dispatchAction (usado pra abrir o
    // drawer Acoes Rapidas via toggleActionMenu(true) quando force-click no
    // hamburger nao funciona — Redux action direto bypassa qualquer issue
    // de event propagation no IconButton MUI).
    dispatch,
});

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(ProductTour));
